import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useResolveFormSchema } from "../../../../../_hooks/useResolveFormSchema";
import fieldsToSections from "../../../../../_utils/fieldsToSections";

/**
 * Builds UserManagementFormModal's field-schema sections from the org's
 * active "userManagement" object customizations (note: object-customization
 * data key stays "userManagement" even though the permission/entity-filter
 * keys are "user" - see the migration plan's "4-way key-naming divergence"
 * note), resolving external references along the way - mirrors every other
 * migrated modal's schema hook, extended with one schema post-processing
 * step the old modal did at render/submit time instead:
 *
 * - Email field: disabled (with an explanatory description) when
 *   mode==="edit" and either the user is already active or their invite
 *   hasn't been sent yet. This is an OR of two conditions - the shared
 *   engine's declarative showCondition/disabledCondition has no
 *   OR-composition primitive, so it's computed here as a plain boolean,
 *   matching Usage Compilation's disabled-computation precedent exactly,
 *   rather than injecting dummy status/isInviteSent fields into form
 *   state purely so a declarative condition could read them (the old
 *   modal's approach).
 *
 * The role field needs no special-casing here anymore - it's a plain
 * singleSelect with `dynamicConfig.referenceType: "roles"`, which
 * `renderInput.jsx` now routes straight to `RoleAsyncSelect` (real
 * server-side search, not a client-fetched, capped-at-some-limit list).
 * It used to be overridden here with `useGetAllRolesQuery({ limit: 1000 })`
 * to paper over the backend's default `limit: 10` truncation - raising the
 * ceiling instead of removing it. Also fixed, as part of the same old
 * override: storing the selected role's *_id* (via isObject/valueSelector/
 * valueDisplay) instead of its *name*, which the old modal stored and
 * converted name->_id at submit time via a hand-built lookup map - fragile
 * if two roles ever shared a name. `RoleAsyncSelect` carries that same
 * isObject-style _id-based value contract natively.
 */
export function useUserManagementFormSchema({ mode, rowData }) {
  const objectCustomizations = useSelector(
    (state) => state?.objectCustomizationFeature?.objectCustomizations
  );

  const userManagementCustomization = useMemo(() => {
    const customization = objectCustomizations?.data?.userManagement;
    return Array.isArray(customization) ? customization : [];
  }, [objectCustomizations]);

  const baseSchema = useMemo(() => {
    const fields = userManagementCustomization.filter(
      (oc) => oc.status === "active" && oc.showDuringCreation
    );
    return fieldsToSections(fields);
  }, [userManagementCustomization]);

  const resolvedSchema = useResolveFormSchema(baseSchema);

  const isEmailDisabled = mode === "edit" && (rowData?.status === "active" || rowData?.isInviteSent === false);
  const emailDisabledDescription = mode === "edit" && rowData?.status === "active"
    ? "Email cannot be changed for active users"
    : mode === "edit" && rowData?.isInviteSent === false
    ? "Email cannot be changed until invite is sent"
    : undefined;

  const processedSchema = useMemo(() => {
    if (!Array.isArray(resolvedSchema)) return [];

    return resolvedSchema.map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => {
        if (field.name === "email" && isEmailDisabled) {
          return { ...field, disabled: true, description: emailDisabledDescription };
        }
        return field;
      }),
    }));
  }, [resolvedSchema, isEmailDisabled, emailDisabledDescription]);

  return useMemo(() => processedSchema, [processedSchema]);
}
